import { useCallback, useEffect, useRef, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Paperclip, MessageSquare, Send, Upload, Download, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const roleTone = {
  admin: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  vendor: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  board: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  resident: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
};

/**
 * RfpThreadPanel — attachments + Q&A thread for a single RFP.
 * Used by both AdminRfpPage and VendorRfpPage.
 */
export function RfpThreadPanel({ rfp }) {
  const { user } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: a }, { data: t }] = await Promise.all([
        api.get(`/rfp/${rfp.id}/attachments`),
        api.get(`/rfp/${rfp.id}/thread`),
      ]);
      setAttachments(a.items || []);
      setMessages(t.items || []);
    } catch (e) { if (typeof console !== 'undefined') console.debug('components/RfpThreadPanel.jsx: swallowed', e); }
  }, [rfp.id]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/rfp/${rfp.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Attached ${file.name}`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Upload failed");
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteAttachment = async (a) => {
    if (!window.confirm(`Delete ${a.filename}?`)) return;
    try {
      await api.delete(`/rfp/attachments/${a.id}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/rfp/${rfp.id}/thread`, { body: text.trim() });
      setText("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to post"); }
    setBusy(false);
  };

  const downloadHref = (att) => `${API}/rfp/attachments/${att.id}/download`;

  return (
    <div className="mt-4 border-t border-white/5 pt-4" data-testid={`rfp-thread-${rfp.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Attachments */}
        <div>
          <div className="overline mb-2 flex items-center gap-1">
            <Paperclip className="h-3 w-3" /> Attachments · {attachments.length}
          </div>
          <input ref={fileRef} type="file" hidden onChange={(e) => upload(e.target.files?.[0])}
                 data-testid={`rfp-attach-file-${rfp.id}`}
                 accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp" />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
                  variant="outline" className="border-white/10 rounded-full mb-2"
                  data-testid={`rfp-attach-btn-${rfp.id}`}>
            <Upload className="h-3 w-3 mr-1" /> {busy ? "Uploading…" : "Attach file"}
          </Button>
          {attachments.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">No attachments yet — drop specs, quotes, or photos here so pricing details stop living in email.</div>
          ) : (
            <div className="space-y-1.5">
              {attachments.map((a) => (
                <div key={a.id} className="rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2 text-xs" data-testid={`rfp-attach-${a.id}`}>
                  <Paperclip className="h-3 w-3 text-zinc-500 shrink-0" />
                  <a href={downloadHref(a)} target="_blank" rel="noreferrer"
                     className="min-w-0 truncate flex-1 text-zinc-100 hover:text-orange-300"
                     data-testid={`rfp-attach-dl-${a.id}`}>{a.filename}</a>
                  <Badge variant="outline" className={roleTone[a.uploaded_by_role] || "border-white/10 text-zinc-400"}>
                    {a.uploaded_by_name}
                  </Badge>
                  <span className="text-[10px] text-zinc-500 tabular-nums">{Math.round((a.size || 0) / 1024)} KB</span>
                  <a href={downloadHref(a)} target="_blank" rel="noreferrer"
                     className="text-zinc-500 hover:text-emerald-300">
                    <Download className="h-3 w-3" />
                  </a>
                  {(user?.role === "admin" || a.uploaded_by === user?.id) && (
                    <button onClick={() => deleteAttachment(a)}
                            className="text-zinc-500 hover:text-red-300"
                            data-testid={`rfp-attach-del-${a.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Thread */}
        <div>
          <div className="overline mb-2 flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> Q&amp;A · {messages.length}
          </div>
          <div className="space-y-1.5 mb-2 max-h-48 overflow-y-auto pr-1">
            {messages.length === 0 ? (
              <div className="text-xs text-zinc-500 italic">Ask a scope question or clarify materials — thread visible only to the admin and invited vendors.</div>
            ) : messages.map((m) => (
              <div key={m.id} className="rounded-md border border-white/10 bg-white/[0.02] p-2" data-testid={`rfp-msg-${m.id}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <User className="h-3 w-3 text-zinc-500" />
                  <span className="text-xs text-zinc-100 font-medium">{m.author_name}</span>
                  <Badge variant="outline" className={roleTone[m.author_role] || "border-white/10 text-zinc-400"}>{m.author_role}</Badge>
                  <span className="text-[10px] text-zinc-500 ml-auto tabular-nums">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-zinc-300 whitespace-pre-wrap">{m.body}</div>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
                      placeholder="Ask a question or answer one…"
                      className="bg-white/[0.04] border-white/10 text-xs"
                      data-testid={`rfp-msg-input-${rfp.id}`} />
            <Button size="sm" onClick={post} disabled={busy || !text.trim()}
                    className="bg-indigo-500 hover:bg-indigo-600 rounded-full"
                    data-testid={`rfp-msg-send-${rfp.id}`}>
              <Send className="h-3 w-3 mr-1" /> Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RfpThreadPanel;
